/*
  The words of the app version log, taken from the prototype's screen (screens/app-version.html
  and scripts/screens/app-version.js) and written in the same voice where it has none. Since
  ALS-160 it is App usage's folded session log, whose own words are APP_USAGE_COPY.log's.
*/
export const APP_SESSIONS_COPY = {
    search: 'Search name, email or IP',
    person: 'Person',
    everyone: 'Everyone',
    findPerson: 'Search people',
    noPerson: 'Nobody of that name signed in',
    action: 'Action',
    anyAction: 'Any action',
    export: 'Export',
    exported: 'Exported',
    /** The log is a support lookup, not a register, and its empty state says so in its own words. */
    noMatch: {
        title: 'No sessions match',
        body: 'Nothing in the log matches the search.',
        filtersBody: 'Nothing in the log matches these filters.',
    },
    columns: {
        name: 'Name',
        email: 'Email',
        platform: 'Platform',
        appVersion: 'App version',
        action: 'Action',
        ip: 'IP address',
        /**
         * Not "Signed in": the column carries the time of whichever action the row records, so on
         * a Logout row that heading read "Signed in 10 Jun 2026, 13:03" and said the opposite of
         * what happened.
         */
        when: 'When',
    },
    /** The legacy records no time on some rows; the column says so rather than showing 1900. */
    noTime: 'Not recorded',
} as const;

/** The two the log holds. The legacy report reads anything that is not a login as a logout. */
export const APP_SESSION_ACTIONS = ['Login', 'Logout'] as const;

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/*
  The words of Reports › App usage (ALS-160), verbatim from the prototype's screens/reports.html
  §6.5 and scripts/screens/reports.js renderAppUsage, and in the same voice where it has none: a
  release list per platform is this build's. Worth a call's reasons are neutral and make no claim
  about lookbooks an old version cannot open, which it can (Franka, 27 Oct 2026).
*/
export const APP_USAGE_COPY = {
    window: 'Window',
    days: (days: number) => `${days} days`,
    hint: {
        range: (from: string, to: string) => `${from} to ${to}`,
        sessions: (count: number) => plural(count, 'session', 'sessions'),
        release: (version: string, on: string) => `latest release ${version} (${on})`,
        releases: (each: string) => `latest release ${each}`,
        rm: (name: string) => `RM ${name}`,
    },
    figures: {
        opened: {
            label: 'Opened the app',
            value: (opened: number, logins: number) => `${opened} of ${logins}`,
            note: 'people with an app login',
        },
        signIns: {
            label: 'Sign-ins',
            note: (perPerson: number) => `${perPerson} per person`,
            nobody: 'nobody signed in',
        },
        onLatest: {
            label: 'On the latest app',
            note: (on: number, of: number, latest: string) => `${on} of ${of} on ${latest}`,
            /** Said in place of a version when the platforms' latest releases differ. */
            theLatest: 'the latest release',
            /** Reports:AppReleases lists nothing yet, so nobody can be on the latest or behind it. */
            noReleases: 'no store release list yet',
        },
        wellBehind: { label: 'Two or more releases behind', nobody: 'nobody' },
        resets: { label: 'Password resets', note: 'from the app' },
        none: 'none',
        empty: '–',
    },
    versions: {
        title: 'App versions in use',
        subtitle: 'people by the version they last opened',
        latest: (version: string) => `${version} · latest`,
        unknown: 'Not recorded',
        behind: (latest: string) =>
            `Anyone below ${latest} has not taken the store update. The app can prompt them on next open; that is the fix, not a call.`,
        current: 'Everyone is on the latest release.',
    },
    devices: {
        title: 'Devices',
        subtitle: 'people by platform · latest device',
        legend: (people: number, share: number) =>
            `${plural(people, 'person', 'people')} · ${share}%`,
    },
    people: (count: number) => plural(count, 'person', 'people'),
    nobodyOpened: 'Nobody opened the app in this window.',
    calls: {
        title: 'Worth a call',
        empty: 'Nothing stands out in this window.',
        showAll: (count: number) => `Show all ${count}`,
        showFirst: (count: number) => `Show first ${count}`,
        tags: {
            ResetPassword: () => 'Reset their password',
            ReleasesBehind: (behind: number) => `${behind} releases behind`,
            NotOpened: (days: number) => `Not opened in ${days} days`,
        },
        why: {
            reset: (ago: string, platform: string | null) =>
                `Password reset ${ago}${platform ? ` on ${platform}` : ''}. Worth checking they got back in.`,
            behind: (version: string, latest: string) =>
                `On ${version}. The latest is ${latest}. Worth asking them to update the app.`,
            /** The API always sends the last sign-in for Not opened; the day is left out only if it did not. */
            notOpened: (day: string | null) =>
                `${day ? `Last sign-in ${day}. ` : ''}Their stylist may want to know before the next lookbook goes out.`,
        },
    },
    list: {
        title: 'People on the app',
        subtitle: (count: number) =>
            `${plural(count, 'person', 'people')} · one line each, not one per sign-in`,
        search: 'Search a name',
        columns: {
            person: 'Person',
            device: 'Device',
            app: 'App',
            signIns: 'Sign-ins',
            lastSeen: 'Last seen',
        },
        lastAction: (ago: string, action: string) => `${ago} · ${action.toLowerCase()}`,
        noMatch: {
            title: 'Nobody matches',
            body: 'No one with an app login matches this name in the window.',
        },
    },
    ago: (days: number) => (days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`),
    log: {
        title: 'Session log',
        note: 'The flat log the current system keeps, one row per sign-in. Everything above is built from it; it stays here for support, who match an IP address or a version against a ticket.',
        count: (count: number) => plural(count, 'session', 'sessions'),
        latest: (shown: number, of: number) => `latest ${shown} of ${of}`,
        showAll: (count: number) => `Show all ${count}`,
        showLatest: (count: number) => `Show latest ${count}`,
        empty: {
            title: 'No sessions',
            body: 'Nothing in the log for this window and relationship manager.',
        },
    },
    failed: {
        title: 'App usage could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
} as const;

/*
  The words of Reports › Client heatmap (ALS-88), verbatim from the prototype's screens/reports.html
  §6.3 and scripts/screens/reports.js renderHeatmap, and in the same voice where it has none: the
  empty grids, the log's search and the failure are this build's. Lookbook page attention (ALS-89)
  takes its title, its "views per page · N weeks", its "Page N", and its picker's place, closed
  label and search from the prototype's #hm-lb-card (ALS-89 QA, 2 Nov 2026); its empty states and
  its removed pages note are this build's (ADR 0105).
*/
export const HEATMAP_COPY = {
    window: 'Activity window',
    weeks: (weeks: number) => `${weeks} weeks`,
    hint: (first: string, last: string, held: number) =>
        `Activity · ${first} to ${last} · the view log holds ${plural(held, 'week', 'weeks')}`,
    nothingLogged: 'Activity · the view log holds nothing for these weeks',
    range: (first: string, last: string) => (first === last ? first : `${first} to ${last}`),
    engagement: {
        title: 'Client engagement',
        subtitle: (range: string | null) =>
            range ? `activity score per client · ${range}` : 'activity score per client',
        client: 'Client',
        signal: 'Signal',
        cell: (client: string, week: string, score: number) =>
            `${client} · ${week} · ${plural(score, 'action', 'actions')}`,
        noActivity: 'no activity',
        empty: 'No client did anything in the app in these weeks.',
        legend: { low: 'Less', high: 'More' },
        call: 'Call today',
        signals: {
            GoneQuiet: 'Gone quiet',
            FirstInterest: 'First interest',
            GoingQuiet: 'Going quiet',
            WarmingUp: 'Warming up',
            Steady: 'Steady',
        },
    },
    demand: {
        title: 'Brand and category demand',
        subtitle: (range: string | null) =>
            range ? `views and orders · ${range}` : 'views and orders',
        brand: 'Brand',
        cell: (brand: string, category: string, count: number) =>
            `${brand} · ${category} · ${count} views and orders`,
        none: 'none',
        empty: 'No product was viewed or ordered in these weeks.',
        legend: { low: 'Low', high: 'High' },
    },
    attention: {
        title: 'Lookbook page attention',
        subtitle: (weeks: number) => `views per page · ${weeks} weeks`,
        picker: 'Lookbook',
        option: (lookbook: string, goLive: string) => `${lookbook} · ${goLive}`,
        views: (views: number) => plural(views, 'view', 'views'),
        search: 'Find a lookbook',
        noMatch: 'No lookbook of that name.',
        none: 'no views',
        nothingOpened: 'No lookbook page was opened in these weeks.',
        notOpened: (lookbook: string) => `No page of ${lookbook} was opened in these weeks.`,
        notOpenedCurrent: (lookbook: string) =>
            `No current page of ${lookbook} was opened in these weeks.`,
        removed: (views: number) => `${plural(views, 'view', 'views')} on pages since removed`,
        failed: {
            title: 'Lookbook page attention could not load',
            body: 'Nothing was lost. Try again in a moment.',
            retry: 'Try again',
        },
    },
    log: {
        title: 'Activity log',
        note: 'The flat log the current system exports. The views above are built from it, and it stays here because it is the source of truth an audit will ask for.',
        count: (count: number) => plural(count, 'entry', 'entries'),
        latest: (shown: number, of: number) => `latest ${shown} of ${of}`,
        showAll: (count: number) => `Show all ${count}`,
        showLatest: (count: number) => `Show latest ${count}`,
        search: 'Search a client',
        none: 'none',
        columns: {
            client: 'Client',
            module: 'Module',
            area: 'Area',
            action: 'Action',
            reference: 'Reference',
            context: 'Context',
            viewedOn: 'Viewed on',
        },
        empty: {
            title: 'No activity logged',
            body: (allManagers: boolean) =>
                allManagers
                    ? 'No client has anything in the view log for the period.'
                    : "This relationship manager's clients have nothing in the view log for the period.",
        },
        noMatch: {
            title: 'No activity matches',
            body: 'No client of that name has anything in the log for these weeks.',
        },
    },
    failed: {
        title: 'The client heatmap could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
} as const;

/*
  The words of the Reports Overview and the dashboard's Sales card (ALS-85), from the prototype's
  screens/reports.html and screens/dashboard.html, and in the same voice where they have none:
  the featured figures, order type and region are the ticket's, not the prototype's.
*/
export const REPORTS_COPY = {
    eyebrow: 'The A List',
    title: 'Reports',
    period: 'Period',
    hint: (current: string, previous: string) => `Highlights · ${current} · vs ${previous}`,
    definition: (label: string) => `What ${label} means`,
    featured: {
        week: 'Sales WTD',
        month: 'Sales MTD',
        year: 'Sales YTD',
        orders: (count: number) => `${count} ${count === 1 ? 'order' : 'orders'}`,
    },
    kpis: {
        revenue: 'Revenue',
        grossProfit: 'Gross profit',
        marginPercentage: 'Gross margin',
        orders: 'Orders',
        averageOrderValue: 'Avg order value',
        units: 'Units',
        // Not in the prototype (ALS-411): says why these two match Transactions › Margin.
        afterRefunds: 'after refunds',
    },
    revenue: {
        title: 'Revenue, gross profit & margin',
        subtitle: (axis: string) => `${axis} · label is margin %`,
        cost: 'Cost',
        // Not in the prototype (ALS-411): the refunds settled in the period, between the two.
        refunds: 'Refunds',
        grossProfit: 'Gross profit',
    },
    top: {
        title: 'Top performers',
        subtitle: (window: string) => `ranked by value · ${window}`,
        tabs: {
            clients: 'Clients',
            brands: 'Brands',
            suppliers: 'Suppliers',
            categories: 'Categories',
        },
        /** Brands, suppliers and categories rank on their product lines, not on whole orders. */
        lineNote: 'Product sales, before shipping and fees',
        empty: 'No sales in this period.',
    },
    occasions: { title: 'Occasions', subtitle: 'orders by special date' },
    channels: { title: 'Sales source', subtitle: 'orders by channel' },
    types: { title: 'Order type', subtitle: 'orders by invoice type' },
    statuses: {
        title: 'Orders by status',
        subtitle: (total: number) => `${total} placed in the period`,
    },
    regions: { title: 'Revenue by region', subtitle: 'by shipping country' },
    emptyOrders: 'No orders in this slice to break down.',
    orders: (count: number) => `${count} ${count === 1 ? 'order' : 'orders'}`,
    failed: {
        title: 'The report could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
    sales: {
        title: 'Sales',
        viewReports: 'View reports',
        revenue: (window: string) => `Revenue · ${window}`,
        /** Names what the change is against: the same days last month for MTD, as the Overview. */
        vsPrevious: (window: string) => `vs ${window}`,
        range: 'Time range',
        pickRange: 'Pick a range',
        tiles: {
            paid: 'Paid',
            orders: 'Orders this period',
            averageOrderValue: 'Avg order value',
            outstanding: 'Outstanding',
        },
        chart: 'Revenue accumulating across the range',
        failed: {
            title: 'Sales could not load',
            body: 'Nothing was lost. Try again in a moment.',
            retry: 'Try again',
        },
        refused: {
            title: 'That range cannot be shown',
            body: 'Pick a range that ends today or earlier.',
        },
    },
} as const;

/*
  The words of Reports' tabs and of Transactions (ALS-86). The tab names, the Orders queue, its
  filters and its columns are the prototype's (screens/reports.html, scripts/screens/reports.js),
  verbatim. Margin, Returns and Products have no frames: their words are the ticket's and the
  21 Oct 2026 sign-off's, in the same voice.
*/
export const TRANSACTIONS_COPY = {
    tabs: {
        overview: 'Overview',
        transactions: 'Transactions',
        clients: 'Clients',
        heatmap: 'Client heatmap',
        brands: 'Brand intelligence',
        app: 'App usage',
    },
    views: {
        orders: 'Orders',
        margin: 'Margin',
        returns: 'Returns',
        products: 'Products',
        tax: 'Tax',
    },
    export: 'Export',
    exported: 'Exported',
    exportFailed: 'The export could not be downloaded. Try again in a moment.',
    failed: {
        title: 'Transactions could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
    orders: {
        card: 'Order queue',
        count: (shown: number, total: number) =>
            shown === total
                ? `${total} ${total === 1 ? 'order' : 'orders'}`
                : `${shown} of ${total} ${total === 1 ? 'order' : 'orders'}`,
        filters: 'Filters',
        search: 'Search order or client',
        buckets: {
            Overdue: 'Overdue',
            Due: 'Due',
            InTransit: 'In transit',
            AtCustoms: 'At customs',
            None: '',
        },
        columns: {
            order: 'Order',
            client: 'Client',
            rm: 'RM',
            value: 'Value',
            status: 'Status',
            payment: 'Payment',
            next: 'Next action',
        },
        next: {
            overdue: (days: number) => `Overdue ${days} ${days === 1 ? 'day' : 'days'}`,
            due: (days: number) =>
                days === 0 ? 'Due today' : `Due in ${days} ${days === 1 ? 'day' : 'days'}`,
            inTransit: 'In transit · DHL',
            atCustoms: 'Held at customs',
        },
        empty: {
            title: 'Nothing in this bucket',
            body: 'No orders match the filters on this queue.',
        },
        drawer: {
            title: 'Filter orders',
            subtitle: 'The subset worth keeping from the legacy report',
            group: 'Order queue',
        },
        filterLabels: {
            status: 'Order status',
            payment: 'Payment',
            rm: 'Relationship manager',
            brand: 'Brand',
            category: 'Category',
            supplier: 'Supplier',
            dates: 'From / To Date',
            any: 'Any',
        },
    },
    margin: {
        kpis: {
            orders: 'Orders',
            averageOrderValue: 'Avg order value',
            marginAfterRefunds: 'Margin after refunds',
            // Before VAT, where Returns' Completed is the same refunds with VAT (Franka, 22 Oct 2026).
            refunds: 'Refunds settled · before VAT',
            averageMargin: 'Avg margin per order',
            marginPercentage: 'Margin %',
        },
        chart: {
            title: 'Margin before and after refunds',
            subtitle: (axis: string) => `${axis} · label is margin % after refunds`,
            before: 'Margin before refunds',
            refunds: 'Refunds before VAT',
            after: 'Margin after refunds',
        },
        table: {
            title: 'By period',
            subtitle: 'refunds come off in the period they were settled',
            period: 'Period',
            orders: 'Orders',
            revenue: 'Revenue',
            averageOrderValue: 'AOV',
            before: 'Margin before refunds',
            refunds: 'Refunds before VAT',
            after: 'Margin after refunds',
            averageMargin: 'Avg margin / order',
            percentage: 'Margin %',
        },
    },
    products: {
        title: 'Margin by product',
        subtitle: (window: string) => `sold or refunded · ${window}`,
        note: "A product's margin is its own lines'. The order's A-List fee belongs to no product, so these do not add up to the order margin.",
        columns: {
            product: 'Product',
            units: 'Units',
            net: 'Net',
            before: 'Margin before refunds',
            refunds: 'Refunds before VAT',
            after: 'Margin after refunds',
            percentage: 'Margin %',
        },
        empty: 'Nothing sold or refunded in this period.',
    },
    returns: {
        stages: {
            requested: 'Requested',
            received: 'Received',
            completed: 'Completed',
            unmatched: 'Unmatched',
            total: 'Total',
        },
        current: 'current',
        thisPeriod: 'this period',
        // Completed is the credit, what the clients got back (Franka, 22 Oct 2026).
        inclVat: 'this period · incl. VAT',
        items: (count: number) => `${count} ${count === 1 ? 'item' : 'items'}`,
        settled: {
            title: 'Settled returns',
            subtitle: (window: string) => `tied to the credit that settled them · ${window}`,
            columns: {
                order: 'Order',
                client: 'Client',
                product: 'Product',
                settledBy: 'Settled by',
                settledOn: 'Settled on',
                amount: 'Amount incl. VAT',
            },
            by: {
                CreditNote: 'Credit note',
                LegacyWalletRefund: 'Legacy refund',
                Unmatched: 'Unmatched',
            },
            empty: 'No returns were settled in this period.',
        },
        // Franka, 22 Oct 2026: history before go-live, kept apart from every period and total.
        beforeGoLive: {
            title: 'Before go-live, not matched to a refund',
            subtitle: (count: number) =>
                `${count} ${count === 1 ? 'item' : 'items'} · in no period or total`,
            note: 'These returns were completed before the new admin went live and no credit note or wallet refund settles them. They were likely refunded outside the system, by bank transfer, in Xero or by hand.',
            columns: { order: 'Order', product: 'Product', amount: 'Amount incl. VAT' },
            empty: 'Every return before go-live is matched to a refund.',
        },
        awaiting: {
            title: 'Awaiting refund',
            subtitle: 'items waiting for credit, against their DHL return parcel',
            columns: {
                order: 'Order',
                client: 'Client',
                product: 'Product',
                status: 'Item status',
                amount: 'Amount',
                check: 'Return parcel',
                days: 'Days since arrival',
            },
            status: { ReturnRequested: 'Return requested', RefundInProgress: 'Refund in progress' },
            checks: {
                ReturnBooked: 'Return booked',
                NoReturnParcel: 'No return parcel',
                ArrivedNotMarked: 'Parcel arrived but not marked received',
                ReadyToCredit: 'Ready to credit',
                ReceivedWithoutParcel: 'Received without a parcel',
                ParcelNotArrived: 'Marked received but parcel not arrived',
            },
            empty: {
                title: 'Nothing awaits a refund',
                body: 'Every returned item has been credited.',
            },
        },
    },
} as const;

/*
  Transactions › Tax (ALS-90). The card, its count, the period control's name and the Period, Net
  sales, VAT, Duties and Total due columns are the prototype's (screens/reports.html, the tax
  panel), verbatim. Refunded VAT, Net taxable and the By tax row card are the ticket's (VAT,
  refunded tax and net taxable, grouped by the tax register's rows), in the same voice. The
  prototype's State column and its Open to reconcile tile are not built: nothing records that a
  period was reconciled.
*/
export const TAX_COPY = {
    period: 'Reconciliation period',
    kpis: {
        netSales: (count: number, period: string) =>
            `Net sales · ${count} ${period.toLowerCase()}${count === 1 ? '' : 's'}`,
        vat: 'VAT',
        duties: 'Duties paid',
        totalDue: 'Total due',
    },
    kpiNotes: {
        netSales: 'before VAT, duties left out',
        vat: 'on the invoices issued',
        duties: 'separate from VAT',
        totalDue: 'VAT less refunds, plus duties',
    },
    reconciliation: {
        title: 'Reconciliation',
        subtitle: 'VAT and duties are separate obligations',
        columns: {
            period: 'Period',
            netSales: 'Net sales',
            vat: 'VAT',
            refundedVat: 'Refunded VAT',
            netTaxable: 'Net taxable',
            duties: 'Duties',
            totalDue: 'Total due',
        },
    },
    rows: {
        title: 'By tax row',
        subtitle: (period: string) => `${period} · as the invoices and credit notes printed it`,
        columns: {
            taxRow: 'Tax row',
            net: 'Net',
            vat: 'VAT',
            refundedNet: 'Refunded net',
            refundedVat: 'Refunded VAT',
            netTaxable: 'Net taxable',
        },
        empty: (period: string) => `No invoices or credit notes were issued in ${period}.`,
    },
    failed: {
        title: 'Tax reconciliation could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
} as const;

/*
  The words of Reports › Clients (ALS-87). Spend is the prototype's Customer spend ("Client 360")
  and New vs returning its Overview card (screens/reports.html, scripts/screens/reports.js), their
  labels, definitions and notes verbatim where the sign-off kept them. Inactive, Drop-off and Cycles
  have no frames: their words are the ticket's and the 22 Oct 2026 sign-off's, in the same voice.
  Each definition is ClientCohorts' on the API and ADR 0092's, said once here.
*/
const clients = (count: number) => `${count} ${count === 1 ? 'client' : 'clients'}`;

export const CLIENTS_COPY = {
    views: {
        spend: 'Spend',
        mix: 'New vs returning',
        inactive: 'Inactive',
        dropoff: 'Drop-off',
        cycles: 'Cycles',
    },
    rm: {
        label: 'Relationship manager',
        all: 'All relationship managers',
    },
    failed: {
        title: 'Clients could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
    clients,
    spend: {
        tiles: {
            active: {
                label: 'Active clients',
                note: (active: number, book: number) => `${active} of ${book} on the book`,
                definition:
                    'Clients whose record is Active. Inactive clients stay in the table but not in this count.',
            },
            lifetime: {
                label: 'Lifetime value (avg)',
                note: 'across active clients',
                definition:
                    'Mean lifetime spend of the active clients who have ordered, after refunds. Averaged over them rather than the whole book, so a prospect who has never bought does not drag it down.',
            },
            atRisk: {
                label: 'At-risk value',
                note: (value: number) => (value > 0 ? 're-engage' : 'nothing at risk'),
                definition: (days: number) =>
                    `Combined lifetime value of the clients with no purchase in the last ${days} days. The money that leaves if nobody calls them.`,
            },
            repeat: {
                label: 'Repeat rate',
                note: (repeat: number, ordered: number) => `${repeat} of ${ordered} who ordered`,
                definition:
                    'Share of clients with more than one order, measured against clients who have ordered at all.',
            },
        },
        card: 'Client 360',
        subtitle: (window: string) => `${window} · refunds and what was actually kept`,
        columns: {
            client: 'Client',
            rm: 'RM',
            orders: 'Orders',
            total: 'Total',
            refunds: 'Refunds incl. VAT',
            actual: 'Actual spend',
            lifetime: 'Lifetime',
            signals: 'Signals',
        },
        signals: {
            New: 'New',
            Inactive: 'Inactive',
            AwaitingPayment: (amount: string) => `Awaiting payment ${amount}`,
            WalletCredit: (amount: string) => `Wallet ${amount} credit`,
        },
        empty: 'No client bought or was refunded in this period.',
    },
    mix: {
        kpis: {
            newClients: 'New clients',
            returningClients: 'Returning clients',
            newSales: 'New client sales',
            returningSales: 'Returning client sales',
        },
        definition:
            "New means the client's first order of all time fell inside the period. Returning means she ordered in the period and had ordered before it. A client is one or the other, never both.",
        card: 'New vs returning',
        subtitle: (window: string) => `${window} · by first order date`,
        new: 'New',
        returning: 'Returning',
        split: (share: number) =>
            `New clients ${share}% of sales, returning clients ${100 - share}%`,
        note: (share: number) =>
            `${share}% of sales came from clients ordering for the first time.`,
        empty: 'No orders in this period.',
        chart: {
            title: 'Margin by new and returning clients',
            subtitle: (axis: string) => `${axis} · margin after refunds`,
        },
        table: {
            title: 'By period',
            subtitle: 'refunds come off the cohort she is in when they settle, before VAT',
            period: 'Period',
            cohort: 'Cohort',
            clients: 'Clients',
            orders: 'Orders',
            revenue: 'Revenue',
            before: 'Margin before refunds',
            refunds: 'Refunds before VAT',
            after: 'Margin after refunds',
        },
    },
    inactive: {
        window: 'Inactive window',
        days: (days: number) => `${days} days`,
        card: 'Inactive clients',
        subtitle: (days: number) => `no purchase in the last ${days} days · as of today`,
        definition:
            'An active client who has bought before, but not in the window ending today. Clients who never bought, and deactivated or disabled clients, are not listed.',
        tiles: {
            count: 'Inactive clients',
            atRisk: 'At-risk value',
        },
        columns: {
            client: 'Client',
            rm: 'RM',
            lastOrder: 'Last order',
            days: 'Days since',
            orders: 'Orders',
            lifetime: 'Lifetime',
        },
        empty: {
            title: 'Nobody has gone quiet',
            body: 'Every active client has bought in this window.',
        },
    },
    dropOff: {
        tiles: {
            lastYear: 'Clients a year before',
            retained: 'Retained',
            droppedOff: 'Dropped off',
            rate: 'Drop-off rate',
        },
        lastYearNote: (label: string) => `bought in ${label}`,
        definition: (thisYear: string, lastYear: string) =>
            `Clients who bought in ${lastYear} and have not bought in ${thisYear}: the same days a year apart.`,
        card: 'Dropped off',
        subtitle: (lastYear: string) => `bought in ${lastYear}, nothing since in the same days`,
        columns: {
            client: 'Client',
            rm: 'RM',
            lastYear: 'Spend a year before',
            lastOrder: 'Last order',
            lifetime: 'Lifetime',
        },
        empty: 'Every client from a year before has bought again.',
    },
    cycles: {
        chart: {
            title: 'Client spend by month',
            subtitle: 'sales less refunds, incl. VAT · last 12 months against the 12 before',
            recent: 'Last 12 months',
            before: '12 months before',
        },
        tiles: {
            gap: 'Usual gap between orders',
            gapValue: (days: number | null) => (days === null ? '–' : `${days} days`),
            gapNote: 'median, across every client',
            soon: 'Peak season starting',
            soonNote: (days: number) => `in the next ${days} days`,
        },
        definition:
            "A client's usual months are the months she bought in in at least two different years. Her peak season is a run of them, and it starts on the 1st of its first month. Her usual gap is the median days between her orders.",
        soon: {
            card: 'Peak season starting soon',
            subtitle: (days: number) => `reach out now · starting in the next ${days} days`,
            empty: 'No client starts a peak season in the coming weeks.',
        },
        months: {
            card: 'Usual buying months',
            subtitle: 'every active client with a buying habit',
            empty: 'No client has bought in the same month in two different years yet.',
        },
        columns: {
            client: 'Client',
            rm: 'RM',
            starts: 'Season starts',
            season: 'Peak season',
            months: 'Usual months',
            gap: 'Usual gap',
            lastOrder: 'Last order',
            lifetime: 'Lifetime',
        },
        allYear: 'All year',
        gapDays: (days: number | null) => (days === null ? '–' : `${days} days`),
    },
} as const;

/*
  The words of Brand intelligence (ALS-91). The privacy note, the tiles' labels and definitions and
  the three no-report states are the prototype's (screens/reports.html, scripts/screens/reports.js),
  verbatim; the definitions are Franka's of 26 Oct 2026. The prompt before a brand is chosen, the
  hints and the reasons a tile has no figure are ours, in the same voice. The charts' titles and
  subtitles (ALS-92) are the prototype's; what a chart with nothing in it says is ours.
*/
export const BRANDS_COPY = {
    brand: 'Brand',
    searchBrands: 'Search brands…',
    noBrand: 'No brand of that name',
    quarter: 'Reporting quarter',
    hint: {
        current: 'current quarter, to date',
        first: 'first captured quarter',
        beforeData: 'before capture began',
        compared: (previous: string) => `compared with ${previous}`,
    },
    choose: {
        title: 'Choose a brand',
        body: 'Search the register for a brand to see how the clientele viewed and bought it, quarter by quarter.',
    },
    privacy: {
        title: 'Anonymised by construction',
        body: 'Every figure here is an aggregate over the clientele. No individual client is identifiable in a brand report, and discretion is what makes the offering saleable.',
    },
    failed: {
        title: 'The brand report could not load',
        body: 'Nothing was lost. Try again in a moment.',
        retry: 'Try again',
    },
    gone: {
        title: 'This brand is no longer on the register',
        body: 'It may have been deactivated since the link was shared. Search the register for another brand.',
    },
    groups: { demand: 'Demand', commercial: 'Commercial' },
    tiles: {
        interest: {
            label: 'Client interest',
            baseline: 'Baseline',
            definition:
                'Change in how much this brand was browsed and ordered by the clientele, against the previous quarter.',
        },
        sellThrough: {
            label: 'Sell-through',
            note: 'on featured',
            toDate: 'on featured · to date',
            definition:
                'Share of the brand pieces in lookbooks published to clients this quarter that sold within 90 days of first being shown. A lookbook counts from its go-live date if it is live now, or if at least one client opened it in the app.',
        },
        topClients: {
            label: 'Top clients engaged',
            value: (engaged: number, of: number) => `${engaged} of ${of}`,
            note: 'engaged',
            definition:
                'How many of the 10 highest-value clients (by spend after refunds over the last 12 months, the top of Clients › Spend) viewed or bought this brand in the quarter.',
        },
        conversion: {
            label: 'Conversion rate',
            definition: 'Share of clients who looked at the brand and then bought from it.',
        },
        averagePrice: {
            label: 'Avg price point',
            definition:
                'Average value of the brand pieces clients actually bought. A piece returned for a full refund was not bought and is left out.',
        },
        repeatRate: {
            label: 'Repeat purchase rate',
            definition:
                "Buyers are the clients who bought the brand in the quarter. Repeat is the share of them who have bought it more than once by the quarter's end.",
        },
        walletShare: {
            label: 'Wallet share',
            definition: 'The brand share of total spend among the clients who buy it.',
        },
    },
    charts: {
        category: {
            title: 'Demand by category',
            subtitle: 'views and orders',
            empty: 'No views or orders in a category this quarter',
        },
        sizes: {
            title: 'Size curve',
            subtitle: 'purchased, not requested',
            empty: "No sizes recorded on this quarter's purchases",
            /** Under the bars (Franka, 30 Oct 2026): "12 pieces with no size recorded". */
            unrecorded: (pieces: number) =>
                `${pieces} ${pieces === 1 ? 'piece' : 'pieces'} with no size recorded`,
        },
        colours: {
            title: 'Colour trends',
            subtitle: 'by colour group',
            empty: "No colour groups recorded on this quarter's purchases",
            unrecorded: (pieces: number) =>
                `${pieces} ${pieces === 1 ? 'piece' : 'pieces'} with no colour recorded`,
        },
        trend: {
            title: 'Interest trend',
            series: 'Interest index',
            /** "six months to Sep 2026 · indexed to 100 at the start", the prototype's. */
            subtitle: (months: string, end: string, toDate: boolean) =>
                `${months} to ${end}${toDate ? ', to date' : ''} · indexed to 100 at the start`,
            months: [
                'one month',
                'two months',
                'three months',
                'four months',
                'five months',
                'six months',
            ],
            /** The trend starts at a full month (Franka, 30 Oct 2026), so this month alone has none. */
            empty: 'No full month of views or orders to index against yet',
        },
    },
    /** A tile with no figure says why, rather than drawing a zero. */
    unavailable: {
        NoPiecesShown: 'No pieces shown in lookbooks this quarter',
        NoViews: 'No app views this quarter',
        NoPurchases: 'No purchases this quarter',
        NoClientSpend: 'No client spend in the last 12 months',
        RefundsExceedSpend: 'Refunds exceed spend',
        TooFewClients: 'Too few clients to show without identifying them',
    },
    change: {
        noPrior: 'no prior quarter',
        interestVs: (previous: string) => `vs ${previous}`,
        nothingBefore: (previous: string) => `no activity in ${previous} to compare with`,
    },
    states: {
        beforeData: {
            title: (quarter: string) => `No report for ${quarter}`,
            body: (first: string | null) =>
                first
                    ? `Brand reporting began in ${first}. Nothing before that quarter was captured, so there is no history to report on rather than a quiet quarter to show.`
                    : 'The app has not recorded a view yet, so there is no history to report on rather than a quiet quarter to show.',
        },
        notStocked: {
            title: 'Nothing to measure',
            body: (brand: string) =>
                `We do not currently stock ${brand}, so there is no activity to report. It will appear here once products are on the books.`,
        },
        notEnoughActivity: {
            title: 'Not enough activity to report',
            body: (pieces: number, brand: string, quarter: string) =>
                `We stock ${pieces} ${pieces === 1 ? 'piece' : 'pieces'} from ${brand}, but ${quarter} holds too little view and order history across the clientele to report on. Figures are shown when a quarter has enough observed activity to stand behind, never estimated to fill the page.`,
        },
    },
} as const;
